import { useEffect, useMemo, useState } from "react";
import toast, { ToastPosition } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { pdfjs } from "react-pdf";
import Loader from "@/atoms-react/loader/Loader.react";
import { useDocumentsConsent } from "@/features/consent/hooks/use-documents-consent";
import { useListDocumentsConsent } from "@/features/consent/hooks/use-list-documents-consent";
import { DocumentConsent } from "@/features/consent/types";
import { Routes } from "@/features/router";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import ConsentDocument, { DocumentAction } from "./ConsentDocument.react";
import { OlkyPassRoutes } from "@/utils/routes";
import { DateTime } from "luxon";
import { usei18n } from "@/features/utils/hooks/use-i18n";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

export default function ConsentDocumentsList() {
  const { t } = useTranslation(["pass"]);
  const { getLanguage } = usei18n();
  const [searchParams] = useSearchParams();
  const {
    isLoading: isLoadingListDocuments,
    data: fetchedDocuments,
    error: listDocumentsError
  } = useListDocumentsConsent();
  const [documents, setDocuments] = useState<DocumentConsent[]>(fetchedDocuments ?? []);
  const [documentAction, setDocumentAction] = useState<DocumentAction | null>(null);
  const navigate = useNavigate();
  const redirectToLvl = searchParams.get("level_to_complete");
  const redirectDashbaord = redirectToLvl
    ? `${OlkyPassRoutes.level}?level_to_complete=${redirectToLvl}`
    : Routes.dashboard;
  const documentConsent = searchParams.get("documentConsent");
  const documentConsentSessionStorage = sessionStorage.getItem("documentConsent");
  //if there is a documentConsent in session storage it means the user was logged in when the email link was clicked:
  //we redirect to this document and load the others to show them after
  //if no documentConsent in session storage it means that the user just logged in so we need to fetch all the documents
  //and show them one by one
  const initialDocument = useMemo(() => {
    if (documentConsent) {
      const foundDocument = fetchedDocuments?.find(
        (doc) => doc.documentConsentUuid === documentConsent
      );
      if (foundDocument) return foundDocument;

      //maybe the uuid is incorrect -> we go the first document
      if (!fetchedDocuments || fetchedDocuments.length === 0) return null;
      navigate({
        pathname: Routes.document_consent,
        search: createSearchParams({
          documentConsent: fetchedDocuments[0].documentConsentUuid
        }).toString()
      });
    }
    return fetchedDocuments && fetchedDocuments.length > 0 ? fetchedDocuments[0] : null;
  }, [fetchedDocuments, documentConsent]);

  const [currentDocument, setCurrentDocument] = useState<DocumentConsent | null>(initialDocument);
  const [isModalConfirmationOpen, setIsModalConfirmationOpen] = useState(false);

  useEffect(() => {
    //documentConsendUuid is in sessionStorage only when the user
    //copy pasted the url for a specific document when logged out
    //once logged in we go in this component and we can remove documentConsent
    //from session storage
    if (documentConsentSessionStorage) {
      sessionStorage.removeItem("documentConsent");
    }
  }, []);

  useEffect(() => {
    if (currentDocument === null && initialDocument !== null) setCurrentDocument(initialDocument);
    if (!documentConsent && initialDocument) {
      navigate({
        pathname: Routes.document_consent,
        search: createSearchParams({
          documentConsent: initialDocument?.documentConsentUuid
        }).toString()
      });
    }
  }, [initialDocument]);

  useEffect(() => {
    setDocuments(fetchedDocuments ?? []);
    if (fetchedDocuments?.length === 0) navigate(redirectDashbaord);
  }, [fetchedDocuments]);

  const { SubmitDocumentConsentMutation } = useDocumentsConsent();
  const { mutateAsync: submitDocument, isPending: isPendingSubmitDocument } =
    SubmitDocumentConsentMutation();

  const onDocumentAction = async ({
    documentConsent,
    documentAction,
    comment
  }: {
    documentConsent: string;
    documentAction: DocumentAction;
    comment?: string;
  }) => {
    setDocumentAction(documentAction);
    if (documents.length === 1) {
      if (documentAction === "cancel") {
        navigate(redirectDashbaord);
      } else {
        await submitDocument({
          documentConsentUuid: documentConsent,
          action: documentAction,
          comment
        });
        navigate(redirectDashbaord);
      }
    } else {
      if (documentAction === "SIGNED" || documentAction === "REFUSED") {
        const res = await submitDocument({
          documentConsentUuid: documentConsent,
          action: documentAction,
          comment: comment
        });
        const time = DateTime.local().setZone("Europe/Luxembourg");

        const formatted = time
          .setLocale(getLanguage())
          .toFormat(getLanguage() === "fr" ? "dd/LL/yyyy 'à' HH:mm" : "dd/LL/yyyy 'at' HH:mm");

        if (!res.success)
          toast.error(t("consent.error_submit_message"), {
            position: "top-left" as ToastPosition
          });
        else {
          if (documentAction === "SIGNED")
            toast.success(t("consent.submit_message", { Date: formatted }), {
              position: "top-left" as ToastPosition
            });
          else if (documentAction === "REFUSED")
            toast.success(t("consent.submit_message", { Date: formatted }), {
              position: "top-left" as ToastPosition
            });
        }
      }
      const submittedDocument = documents.find(
        (doc) => doc.documentConsentUuid === documentConsent
      );
      if (!submittedDocument) {
        navigate(redirectDashbaord);
        return;
      }
      documents.splice(documents.indexOf(submittedDocument), 1);
      if (documents.length > 0) {
        setCurrentDocument(documents[0]);
        setIsModalConfirmationOpen(false);
        navigate({
          pathname: Routes.document_consent,
          search: createSearchParams({
            documentConsent: documents[0].documentConsentUuid
          }).toString()
        });
      } else {
        navigate(redirectDashbaord);
      }
    }
  };

  if (listDocumentsError) toast.error(t("consent.list_documents_error"));

  if (isLoadingListDocuments)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    );

  return (
    currentDocument && (
      <ConsentDocument
        onDocumentAction={onDocumentAction}
        contentBase64={currentDocument.contentBase64}
        isPendingConfirmDocument={isPendingSubmitDocument && documentAction === "SIGNED"}
        isPendingRejectDocument={isPendingSubmitDocument && documentAction === "REFUSED"}
        isModalConfirmationOpen={isModalConfirmationOpen}
      />
    )
  );
}
